import { Link, useNavigate } from 'react-router-dom';
import { Sparkles, ClipboardList, LayoutDashboard, Activity, AlertTriangle, ContrastIcon, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useBusinessConfig } from '../hooks/useBusinessConfig';
import { useState, type ReactNode } from 'react';
import { PublicFooter } from './PublicFooter';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase/client';

interface LayoutProps {
  isAdmin: boolean;
  setIsAdmin: (isAdmin: boolean) => void;
  children: ReactNode;
}

export const Layout = ({ isAdmin, setIsAdmin, children }: LayoutProps) => {
  const { config } = useBusinessConfig();
  const navigate = useNavigate();
  const [sosSending, setSosSending] = useState(false);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setIsAdmin(false);
    toast.success('Logged out successfully');
    navigate('/');
  };

  // Initialize mode from localStorage using lazy initialization
  const [mode, setMode] = useState<'support' | 'focus' | 'full'>(() => {
    const savedMode = localStorage.getItem('appMode') as 'support' | 'focus' | 'full' | null;
    return savedMode || 'support';
  });

  const updateMode = (newMode: 'support' | 'focus' | 'full') => {
    setMode(newMode);
    localStorage.setItem('appMode', newMode);
  };

  const triggerSOS = async () => {
    setSosSending(true);

    // Get regional emergency number - auto-detect based on locale
    const settings = JSON.parse(localStorage.getItem('globalSettings') || '{}');
    let emergencyNumber = settings.sos_primary_number;

    if (!emergencyNumber) {
      const userLocale = navigator.language || 'en-AU';
      const regionMap: Record<string, string> = {
        'AU': '000',
        'US': '911',
        'CA': '911',
        'GB': '999',
        'EU': '112',
        'DE': '112',
        'FR': '112',
        'IT': '112',
        'ES': '112',
        'NZ': '111',
        'SG': '999',
        'HK': '999',
        'IN': '112',
        'ZA': '10111',
      };

      // Extract country code from locale
      const countryCode = userLocale.split('-')[1] || userLocale.split('_')[1] || 'AU';
      emergencyNumber = regionMap[countryCode.toUpperCase()] || '000';
    }

    setSosSending(false);

    // No backend API available - guide user to call emergency services directly
    toast.error(
      `🚨 SOS Feature requires backend. Call ${emergencyNumber} directly!`,
      { duration: 10000 }
    );

    // Offer to dial emergency number directly
    if (confirm(`Call emergency services (${emergencyNumber}) now?`)) {
      window.location.href = `tel:${emergencyNumber}`;
    }
  };

  // Determine mode states
  const isSupportMode = mode === 'support';
  const isFocusMode = mode === 'focus';

  return (
    <div className={`min-h-screen flex flex-col font-sans ${
      isSupportMode 
        ? 'text-2xl leading-relaxed bg-amber-50' // Support mode: larger text, high contrast
        : ''
    }`}>
      {/* Top Header - Enhanced visibility in Support Mode */}
      <header className={`sticky top-0 z-50 w-full border-b ${
        isSupportMode 
          ? 'bg-amber-100/90 backdrop-blur-md border-amber-300' 
          : 'bg-white/90 backdrop-blur-md'
      }`}>
        <div className="flex h-16 items-center justify-between px-4 max-w-6xl mx-auto">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <div className="bg-slate-900 p-1.5 rounded-lg">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div className="flex flex-col leading-none">
              <span className={`font-black text-xl tracking-tighter uppercase ${
                isSupportMode ? 'text-2xl' : ''
              }`}>
                {config?.biz_name}
              </span>
              <span className={`text-[10px] font-bold text-purple-600 tracking-widest uppercase ${
                isSupportMode ? 'text-lg' : ''
              }`}>
                {config?.biz_type} System
              </span>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size={isSupportMode ? "lg" : "sm"}
              onClick={() => {
                if (isSupportMode) updateMode('focus');
                else if (isFocusMode) updateMode('full');
                else updateMode('support');
              }}
              className={`flex items-center gap-1 ${
                isSupportMode
                  ? 'border-2 border-purple-600 text-purple-800 font-black'
                  : ''
              }`}
            >
              {isSupportMode ? (
                <>
                  <LayoutDashboard className="w-4 h-4" />
                  <span>Focus Mode</span>
                </>
              ) : isFocusMode ? (
                <>
                  <Sparkles className="w-4 h-4" />
                  <span>Full Mode</span>
                </>
              ) : (
                <>
                  <ContrastIcon className="w-4 h-4" />
                  <span>Support Mode</span>
                </>
              )}
            </Button>
            {isAdmin ? (
              <Button
                variant="outline"
                size={isSupportMode ? "lg" : "sm"}
                onClick={handleLogout}
                className="flex items-center gap-2 text-red-600 hover:text-red-700"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </Button>
            ) : (
              <Button
                variant="outline"
                size={isSupportMode ? "lg" : "sm"}
                onClick={() => navigate('/login')}
                className="flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4" />
                <span>Staff Login</span>
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className={`max-w-6xl mx-auto p-4 flex-grow w-full ${
        isSupportMode ? 'pb-32 text-2xl' : 'pb-24'
      }`}>
        {children}
      </main>

      {/* Footer only shows on public pages (not in admin mode) */}
      {!isAdmin && <PublicFooter />}

      {/* Bottom Nav for Admin/Staff - Enhanced in Support Mode */}
      {isAdmin && (
        <nav className={`fixed bottom-0 left-0 right-0 ${
          isSupportMode ? 'h-20' : 'h-16'
        } bg-white border-t flex justify-around items-center px-4 z-50`}>
          <Link to="/staff-tool" className="flex flex-col items-center text-slate-400 hover:text-purple-600">
            {config?.biz_type === 'support_worker' ? (
              <Activity className={`${isSupportMode ? 'w-8 h-8' : 'w-6 h-6'}`} />
            ) : (
              <ClipboardList className={`${isSupportMode ? 'w-8 h-8' : 'w-6 h-6'}`} />
            )}
            <span className={`text-[10px] font-bold uppercase tracking-tighter ${
              isSupportMode ? 'text-lg mt-1' : ''
            }`}>
              {config?.biz_type === 'support_worker' ? 'Meds' : 'Tasks'}
            </span>
          </Link>
          <Link to="/admin" className="flex flex-col items-center text-slate-400 hover:text-purple-600">
            <LayoutDashboard className={`${isSupportMode ? 'w-8 h-8' : 'w-6 h-6'}`} />
            <span className={`text-[10px] font-bold uppercase tracking-tighter ${
              isSupportMode ? 'text-lg mt-1' : ''
            }`}>
              Admin
            </span>
          </Link>
        </nav>
      )}

      {/* SOS Emergency Button - Enhanced visibility */}
      {isAdmin && (
        <button
          onClick={triggerSOS}
          disabled={sosSending}
          className={`fixed bottom-20 right-6 ${
            isSupportMode
              ? 'w-20 h-20 border-8'  // Support mode: giant button
              : isFocusMode
                ? 'w-16 h-16 border-4' // Focus mode: large button
                : 'w-12 h-12 border-2' // Full mode: standard button
          } ${
            sosSending
              ? 'bg-red-400 cursor-not-allowed'
              : 'bg-red-600 hover:bg-red-700 animate-pulse'
          } text-white rounded-full shadow-2xl flex items-center justify-center z-[100] border-white transition-all`}
          aria-label="Emergency SOS Button"
        >
          {sosSending ? (
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white" />
          ) : (
            <AlertTriangle className={isSupportMode ? "w-10 h-10" : isFocusMode ? "w-8 h-8" : "w-6 h-6"} />
          )}
        </button>
      )}
    </div>
  );
};